// Phase 11 Step 1 — useLeagues hook.
//
// Read-only consumer of the leagues table. Returns all configured
// leagues for the authenticated user, with region/tier filtering.
//
// Trust contract:
//   - Read via RLS-scoped SELECT. Writes via upsert_league RPC only.
//   - Defensive parsing — missing fields render with fallbacks.
//   - No localStorage. No external HTTP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export type CoverageTier = "tier_1" | "tier_2";
export type LeagueStatus = "active" | "paused" | "coming_soon" | "retired";

export interface League {
  id: string;
  name: string;
  short_name: string;
  country: string;
  region: string;
  coverage_tier: CoverageTier;
  status: LeagueStatus;
  session_window: string | null;
  confidence_floor: number;
  agent_count: number;
  data_sources: readonly string[];
  meta: Record<string, unknown>;
  simulated: boolean;
  created_at: string;
  updated_at: string;
}

export interface UseLeaguesState {
  leagues: readonly League[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Row parser ────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function parseLeague(row: Record<string, unknown>): League {
  return {
    id: String(row.id ?? ""),
    name: String(row.name ?? ""),
    short_name: String(row.short_name ?? ""),
    country: String(row.country ?? ""),
    region: String(row.region ?? ""),
    coverage_tier: (row.coverage_tier as CoverageTier) ?? "tier_2",
    status: (row.status as LeagueStatus) ?? "coming_soon",
    session_window: asStr(row.session_window),
    confidence_floor: typeof row.confidence_floor === "number"
      ? row.confidence_floor
      : Number(row.confidence_floor ?? 0.6),
    agent_count: typeof row.agent_count === "number" ? row.agent_count : 18,
    data_sources: Array.isArray(row.data_sources)
      ? (row.data_sources as string[])
      : [],
    meta:
      typeof row.meta === "object" && row.meta !== null
        ? (row.meta as Record<string, unknown>)
        : {},
    simulated: true,
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

// ─── Static fallback leagues ──────────────────────────────────

export const DEFAULT_LEAGUES: readonly League[] = [
  {
    id: "seed-bra-serie-a",
    name: "Brazilian Serie A",
    short_name: "BRA-A",
    country: "Brazil",
    region: "south_america",
    coverage_tier: "tier_1",
    status: "active",
    session_window: "15:00-03:00 Accra",
    confidence_floor: 0.62,
    agent_count: 18,
    data_sources: ["sofascore", "flashscore", "fbref"],
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: "seed-arg-primera",
    name: "Argentine Primera Division",
    short_name: "ARG-1",
    country: "Argentina",
    region: "south_america",
    coverage_tier: "tier_1",
    status: "active",
    session_window: "22:00-04:00 UTC",
    confidence_floor: 0.62,
    agent_count: 18,
    data_sources: ["sofascore", "flashscore", "fbref"],
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: "seed-copa-lib",
    name: "Copa Libertadores",
    short_name: "LIBER",
    country: "South America",
    region: "south_america",
    coverage_tier: "tier_1",
    status: "active",
    session_window: "22:00-05:00 UTC",
    confidence_floor: 0.65,
    agent_count: 18,
    data_sources: ["sofascore", "flashscore"],
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: "seed-copa-sud",
    name: "Copa Sudamericana",
    short_name: "SUDAM",
    country: "South America",
    region: "south_america",
    coverage_tier: "tier_2",
    status: "active",
    session_window: "22:00-05:00 UTC",
    confidence_floor: 0.68,
    agent_count: 12,
    data_sources: ["sofascore", "flashscore"],
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: "seed-mls",
    name: "Major League Soccer",
    short_name: "MLS",
    country: "USA",
    region: "north_america",
    coverage_tier: "tier_1",
    status: "active",
    session_window: "23:00-05:00 UTC",
    confidence_floor: 0.60,
    agent_count: 18,
    data_sources: ["sofascore", "flashscore", "fbref", "americansoccer"],
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  },
  {
    id: "seed-liga-mx",
    name: "Liga MX",
    short_name: "LMX",
    country: "Mexico",
    region: "north_america",
    coverage_tier: "tier_1",
    status: "active",
    session_window: "01:00-06:00 UTC",
    confidence_floor: 0.62,
    agent_count: 18,
    data_sources: ["sofascore", "flashscore", "fbref"],
    meta: {},
    simulated: true,
    created_at: "",
    updated_at: "",
  },
];

// ─── Hook ──────────────────────────────────────────────────────

export function useLeagues(
  opts: {
    regionFilter?: string;
    statusFilter?: LeagueStatus;
  } = {},
): UseLeaguesState {
  const { user } = useAuthSession();
  const [leagues, setLeagues] = useState<readonly League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setLeagues([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      let query = (
        client.from("leagues" as never) as unknown as {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
              order: (col: string, opts: { ascending: boolean }) => Promise<{
                data: Record<string, unknown>[] | null;
                error: { message: string } | null;
              }>;
            };
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      ).select("*");

      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (opts.regionFilter && opts.statusFilter) {
        resp = await query
          .eq("region", opts.regionFilter)
          .eq("status", opts.statusFilter)
          .order("name", { ascending: true });
      } else if (opts.regionFilter) {
        resp = await query
          .eq("region", opts.regionFilter)
          .order("name", { ascending: true });
      } else if (opts.statusFilter) {
        resp = await query
          .eq("status", opts.statusFilter)
          .order("name", { ascending: true });
      } else {
        resp = await query.order("name", { ascending: true });
      }

      if (cancelled) return;

      if (resp.error) {
        // Fallback to static defaults on error
        setLeagues(DEFAULT_LEAGUES);
        setError(null);
        setLoading(false);
        return;
      }

      const parsed = (resp.data ?? [])
        .filter((r): r is Record<string, unknown> => r !== null)
        .map(parseLeague);

      // If no DB rows, use static defaults
      setLeagues(parsed.length > 0 ? parsed : DEFAULT_LEAGUES);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, opts.regionFilter, opts.statusFilter, tick]);

  return useMemo(
    () => ({ leagues, loading, error, refetch }),
    [leagues, loading, error, refetch],
  );
}
